import React from "react";
import Icon from "../../lib/icon";
import BadgeBonus from "../BadgeBonus/BadgeBonus";
import BadgeTime from "../BadgeTime/BadgeTime";
import "./ProductCard.scss";
import ApiImage from "../ApiImage";
import Button from "../Button";

export default function ProductCards({
  products,
  sectionTitle,
  showCount = false,
  layout = "horizontal",
  showViewAll = true,
  onProductPress,
  onViewAllPress,
  imageWidth = 130,
  imageHeight = 130,
  cardWidth = 130,
  backgroundColor = "transparent",
  cardBackgroundColor = "none",
  borderRadius = 0,
  count = null,
  favoriteEnable = true
}) {
  function getProductImage(product) {
    if (product?.photo) {
      return product.photo;
    }
    if (Array.isArray(product?.photos) && product.photos.length > 0) {
      return product.photos[0];
    }
    return undefined;
  }

  function getOptimalFontSizes(price, originalPrice, containerWidth) {
    const basePriceSize = 17;
    const baseOrigSize = 11;
    const minPriceSize = 12;
    const minOrigSize = 9;

    const priceWidth = price.length * basePriceSize * 0.6;
    const origWidth = originalPrice
      ? originalPrice.length * baseOrigSize * 0.6 + 8
      : 0;

    const total = priceWidth + origWidth;

    if (total <= containerWidth)
      return { priceSize: basePriceSize, originalPriceSize: baseOrigSize };

    const factor = containerWidth / total;

    return {
      priceSize: Math.max(minPriceSize, Math.floor(basePriceSize * factor)),
      originalPriceSize: Math.max(minOrigSize, Math.floor(baseOrigSize * factor)),
    };
  }

  // 🔹 Цена с адаптивными размерами
  function AdaptivePrice({ price, originalPrice, hasDiscount, containerWidth }) {
    const { priceSize, originalPriceSize } = getOptimalFontSizes(
      price,
      originalPrice,
      containerWidth
    );

    return (
      <div className="flex items-center my-1 flex-nowrap price">
        <span
          className={`font-inter-18-semi-bold ${
            hasDiscount ? "text-[#D62F50]" : "text-[#2D1D21]"
          }`}
          style={{ fontSize: 17 }}
        >
          {price}
        </span>

        {hasDiscount && (
          <span
            className="font-inter-18-semi-bold text-[#C6B2B6] line-through ml-2"
            style={{ fontSize: originalPriceSize }}
          >
            {originalPrice}
          </span>
        )}
      </div>
    );
  }

  // 🔹 Рендер карточки
  function renderProductCard(product, customWidth) {
    const cardW = customWidth || cardWidth;
    const widthNum = typeof cardW === "number" ? cardW : 130;
    const textWidth = widthNum - 32;

    return (
      <div
        key={product.id}
        className="cursor-pointer product-cards__item"
      >
        <div
          className="overflow-hidden product-cards__card"
          style={{ backgroundColor: cardBackgroundColor }}
        >
          {/* Image */}
          <div className="flex justify-center items-center relative product-cards__image-wrapper">
            <a onClick={() => onProductPress?.(product.id)}>
              <ApiImage
                src={getProductImage(product)}
                alt=""
                className={`product-cards__image ${product.hasBadge && "img_badge"}`}
                style={{ objectFit: "cover" }}
              />
            </a>
            

            {/* Badge */}
            {product.hasBadge && (
              <div className="bg-white product-cards__badge">
                <span className="text-[9px] font-inter-18-bold text-[#007BDF] ">
                  {product.badgeType || (product.id <= 3 ? "Акция" : "Новинка")}
                </span>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="px-2 product-cards__content" onClick={() => onProductPress?.(product.id)}>
            {/* Top badges */}
            <div className="flex flex-row my-1 product-cards__badges">
              {product.bonus > 0 ? (<div className="mr-1">
                <BadgeBonus size="small" value={product.bonus} />
              </div>) : (<></>)}
              
              <BadgeTime size="small" label="10-15 мин" />
            </div>

            {/* Price */}
            <AdaptivePrice
              price={product.price}
              originalPrice={product.originalPrice}
              hasDiscount={product.hasDiscount}
              containerWidth={textWidth}
            />

            {/* Description */}
            {product.title && (
              <div className="text-[11px] font-inter-18-light text-[#2D1D21] mb-3 leading-4 line-clamp-2 product-cards__description">
                {product.title}
              </div>
            )}

            {/* Rating */}
            <div className="flex flex-row items-center mb-3 product-cards__rating">
              <Icon name="star-filled" color="#FE9402" width={12} height={12} />
              <span className="text-[11px] font-inter-18-semi-bold text-[#2D1D21] ml-1">
                {product.rating}
              </span>

              <div className="w-1 h-1 bg-[#AD9096] rounded-full mx-1" />

              <span className="text-[11px] font-inter-18-light text-[#AD9096]">
                {product.reviews} Оценок
              </span>
            </div>

            <div className="product-cards__buy-button-wrapper">
              <Button className="btn-primary">Открыть</Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 🔹 Layout variations

  function renderHorizontal() {
    return (
      <div
        className="flex overflow-x-auto gap-4 product-cards__list-horizontal"
        style={{ backgroundColor }}
      >
        {products.map((p) => renderProductCard(p))}
      </div>
    );
  }

  function renderGrid() {
    return (
      <div className="product-cards__list-grid" style={{ backgroundColor }}>
        <div className="flex flex-wrap justify-between">
          {products.map((p) => (
            <div key={p.id} className="w-[48%] mb-4 product-cards__item">
              {renderProductCard(p, "100%")}
            </div>
          ))}
        </div>
      </div>
    );
  }

  function renderVertical() {
    return (
      <div className="px-4 product-cards__list-vertical" style={{ backgroundColor }}>
        <div className="flex flex-col gap-4">
          {products.map((p) => (
            <div key={p.id} className="product-cards__item">
              {renderProductCard(p, "100%")}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className="product-cards"
      style={{
        backgroundColor,
        borderRadius,
        overflow: borderRadius > 0 ? "hidden" : "visible",
      }}
    >
      {/* Header */}
      <div className="flex flex-row justify-between items-center product-cards__header">
        <div className="flex items-center product-cards__header-title">
          <span className="text-[20px] font-inter-18-semi-bold text-[#2D1D21]">
            {sectionTitle}
          </span>

          {showCount && (
            <span className="ml-2 text-[14px] text-[#8a8a8a] product-cards__header-count">
              {count == null ? products.length : count}
            </span>
          )}
        </div>

        {showViewAll && (
          <button
            className="flex items-center bg-[#FBEAEE] rounded-full h-[30px] w-[70px] product-cards__view-all-button"
            onClick={onViewAllPress}
          >
            <span className="text-[15px] text-[#D62F50] mr-1 ml-2 product-cards__view-all-text">
              Все
            </span>
            <Icon
              name="chevron-right"
              width={22}
              height={22}
              color="#D62F50"
              className="product-cards__view-all-icon"
            />
          </button>
        )}
      </div>

      {layout === "horizontal" && renderHorizontal()}
      {layout === "grid" && renderGrid()}
      {layout === "vertical" && renderVertical()}
    </div>
  );
}